/**
 * Даты календаря праздников: диапазон «с … по …» и государственные праздники.
 *
 * Чистые функции на строках `YYYY-MM-DD` — без часовых поясов: дата праздника — это
 * число в календаре, а не момент времени, и `new Date('2026-03-21')` в Ташкенте уже
 * была бы полночью UTC, то есть пятью часами раньше.
 */

const pad = (n: number) => String(n).padStart(2, '0');

function parts(iso: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/** День недели даты: 0 — пн … 6 — вс (как `weekday` графика). */
export function weekdayOf(iso: string): number | null {
  const p = parts(iso);
  if (!p) {
    return null;
  }
  const d = new Date(Date.UTC(p[0], p[1] - 1, p[2]));
  return (d.getUTCDay() + 6) % 7;
}

/** Самый длинный диапазон за один раз: месяц отпуска, а не опечатка в годе. */
export const MAX_RANGE_DAYS = 31;

/**
 * Все даты от `from` до `to` включительно. `to` раньше `from` — только `from`.
 * Длиннее `MAX_RANGE_DAYS` — обрезается: лишний год записей, добавленный опечаткой,
 * пришлось бы удалять по одной.
 */
export function expandRange(from: string, to: string | null | undefined): string[] {
  const a = parts(from);
  if (!a) {
    return [];
  }
  const b = to ? parts(to) : null;
  const start = Date.UTC(a[0], a[1] - 1, a[2]);
  const end = b ? Date.UTC(b[0], b[1] - 1, b[2]) : start;
  const out: string[] = [];
  for (let t = start; t <= Math.max(start, end) && out.length < MAX_RANGE_DAYS; t += 86_400_000) {
    const d = new Date(t);
    out.push(`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`);
  }
  return out;
}

/**
 * Нерабочие праздники Узбекистана с постоянной датой (Трудовой кодекс). Два Хайита —
 * Рамазан и Курбан — по лунному календарю: их даты объявляются каждый год отдельно,
 * поэтому здесь их нет и форма просит добавить их вручную.
 */
export const UZ_FIXED_HOLIDAYS: ReadonlyArray<{monthDay: string; key: string}> = [
  {monthDay: '01-01', key: 'newYear'},
  {monthDay: '03-08', key: 'womensDay'},
  {monthDay: '03-21', key: 'navruz'},
  {monthDay: '05-09', key: 'remembrance'},
  {monthDay: '09-01', key: 'independence'},
  {monthDay: '10-01', key: 'teachers'},
  {monthDay: '12-08', key: 'constitution'},
];

/** Праздники года, которых ещё нет среди `existing` дат. */
export function missingHolidays(
  year: number,
  existing: ReadonlyArray<string>,
): Array<{date: string; key: string}> {
  const have = new Set(existing);
  return UZ_FIXED_HOLIDAYS.map(h => ({date: `${year}-${h.monthDay}`, key: h.key})).filter(
    h => !have.has(h.date),
  );
}
